/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Col, Row, Table, Badge, Input, InputGroup, InputGroupText } from 'reactstrap';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId } from '../../constants';
import profitabilityService from '../../services/profitability';
import NoRecords from '../../components/Table/NoRecords';

const MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const fmt = (v) =>
  v == null ? '—' : Number(v).toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <i className="ri-arrow-up-down-line ms-1 text-muted opacity-50" />;
  return sortDir === 'asc'
    ? <i className="ri-arrow-up-s-fill ms-1 text-primary" />
    : <i className="ri-arrow-down-s-fill ms-1 text-primary" />;
};

const MonthlyReport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const companyId = LocalstorageService.getItem(CompanyId);
  const now = new Date();

  const [month, setMonth]     = useState(now.getMonth() + 1);
  const [year, setYear]       = useState(now.getFullYear());
  const [list, setList]       = useState([]);
  const [search, setSearch]   = useState('');
  const [sortField, setSortField] = useState('employeeName');
  const [sortDir, setSortDir] = useState('asc');
  const [groupBy, setGroupBy] = useState('employee'); // 'employee' | 'group' | 'none'

  useEffect(() => {
    setPageTitle(t('PROFITABILITY.MONTHLY_REPORT_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.MONTHLY_REPORT_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.MONTHLY_REPORT_PAGE.CRUMB_ITEM'), link: '#' },
    ]);
  }, []);

  const fetchReport = async (rebuild = false) => {
    dispatch(setLoadingOverlay(true));
    try {
      if (rebuild) {
        await profitabilityService.rebuildMonthlyStats({ month, year });
        toast.info(t('PROFITABILITY.MONTHLY_REPORT_PAGE.TOAST_REBUILT'));
      }
      const res = await profitabilityService.getProfitabilityReport({ companyId, month, year });
      setList(res.data?.dataList || []);
    } catch {
      toast.error(t('PROFITABILITY.MONTHLY_REPORT_PAGE.TOAST_ERROR_LOAD'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  useEffect(() => { fetchReport(false); }, [month, year]);

  // סינון חיפוש
  const filtered = useMemo(() => {
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter(r =>
      (r.locationName  || '').toLowerCase().includes(q) ||
      (r.terminalNumber|| '').toLowerCase().includes(q) ||
      (r.employeeName  || '').toLowerCase().includes(q) ||
      (r.groupName     || '').toLowerCase().includes(q)
    );
  }, [list, search]);

  // מיון
  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortField] ?? 0;
      let bv = b[sortField] ?? 0;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      // secondary sort: location name
      if (sortField !== 'locationName') {
        const an = (a.locationName || '').toLowerCase();
        const bn = (b.locationName || '').toLowerCase();
        return an < bn ? -1 : an > bn ? 1 : 0;
      }
      return 0;
    });
    return arr;
  }, [filtered, sortField, sortDir]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // קיבוץ לפי עובד / ריכוז
  const grouped = useMemo(() => {
    if (groupBy === 'none') return null;
    const key = groupBy === 'employee'
      ? r => r.employeeName || t('PROFITABILITY.MONTHLY_REPORT_PAGE.NO_EMPLOYEE')
      : r => r.groupName    || t('PROFITABILITY.MONTHLY_REPORT_PAGE.NO_CONCENTRATION');
    const map = {};
    for (const row of sorted) {
      const k = key(row);
      if (!map[k]) map[k] = [];
      map[k].push(row);
    }
    return map;
  }, [sorted, groupBy]);

  // סיכום כולל
  const sumRows = (rows) => rows.reduce((acc, r) => ({
    transactions:    acc.transactions    + (r.transactions    || 0),
    amountProcessed: acc.amountProcessed + (r.amountProcessed || 0),
    revenue:         acc.revenue         + (r.revenue         || 0),
    locationCost:    acc.locationCost    + (r.locationCost    || 0),
    casponetCost:    acc.casponetCost    + (r.casponetCost    || 0),
    costOfMoney:     acc.costOfMoney     + (r.costOfMoney     || 0),
    commCost:        acc.commCost        + (r.commCost        || 0),
    totalCost:       acc.totalCost       + (r.totalCost       || 0),
    profit:          acc.profit          + (r.profit          || 0),
  }), { transactions: 0, amountProcessed: 0, revenue: 0, locationCost: 0,
        casponetCost: 0, costOfMoney: 0, commCost: 0, totalCost: 0, profit: 0 });

  const totals = useMemo(() => sumRows(filtered), [filtered]);

  const exportExcel = () => {
    const rows = list.map(r => ({
      עובד: r.employeeName || '',
      מסוף: r.terminalNumber, מקום: r.locationName,
      ריכוז: r.groupName || '',
      פעולות: r.transactions, 'סכום שעבר': r.amountProcessed,
      'דמי שימוש כולל מע"מ': r.revenueWithVAT,
      'הכנסה (לפני מע"מ)': r.revenue,
      'עלות מקום': r.locationCost,
      כספונט: r.casponetCost,
      'עלות הכסף': r.costOfMoney,
      תקשורת: r.commCost,
      'סה"כ עלויות': r.totalCost, 'רווח מכונה': r.profit,
      '%': r.profitPct,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'רווחיות');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf]), `רווחיות-${month}-${year}.xlsx`);
  };

  const Th = ({ field, children, className = '' }) => (
    <th className={`${className} cursor-pointer user-select-none`}
        style={{ cursor: 'pointer' }}
        onClick={() => toggleSort(field)}>
      {children}<SortIcon field={field} sortField={sortField} sortDir={sortDir} />
    </th>
  );

  const renderRow = (r, idx) => (
    <tr key={r.terminalNumber || idx}>
      {groupBy === 'none' && <td className="text-muted small">{r.employeeName || '—'}</td>}
      <td>{r.terminalNumber}</td>
      <td>{r.locationName}</td>
      <td className="text-end">{r.transactions?.toLocaleString()}</td>
      <td className="text-end">{fmt(r.amountProcessed)}</td>
      <td className="text-end">{fmt(r.revenue)}</td>
      <td className="text-end">{fmt(r.locationCost)}</td>
      <td className="text-end">{fmt(r.casponetCost)}</td>
      <td className="text-end">{fmt(r.costOfMoney)}</td>
      <td className="text-end">{fmt(r.commCost)}</td>
      <td className="text-end">{fmt(r.totalCost)}</td>
      <td className={`text-end fw-bold ${r.profit >= 0 ? 'text-success' : 'text-danger'}`}>
        {fmt(r.profit)}
      </td>
      <td className="text-end">{r.profitPct}%</td>
    </tr>
  );

  const renderGroupHeader = (groupLabel, rows) => {
    const gt = sumRows(rows);
    return (
      <tr key={`grp-${groupLabel}`} className="table-secondary fw-bold">
        <td colSpan={2} className="text-primary">
          <i className="ri-group-line me-1"></i>{groupLabel}
          <Badge color="secondary" className="ms-2">{rows.length}</Badge>
        </td>
        <td className="text-end">{gt.transactions?.toLocaleString()}</td>
        <td className="text-end">{fmt(gt.amountProcessed)}</td>
        <td className="text-end">{fmt(gt.revenue)}</td>
        <td></td><td></td><td></td><td></td>
        <td className="text-end">{fmt(gt.totalCost)}</td>
        <td className={`text-end ${gt.profit >= 0 ? 'text-success' : 'text-danger'}`}>
          {fmt(gt.profit)}
        </td>
        <td></td>
      </tr>
    );
  };

  const colSpanTotal = groupBy === 'none' ? 2 : 2;
  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  return (
    <React.Fragment>
      {/* שורת כלים */}
      <Row className="mb-3 align-items-end g-2">
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.MONTH')}</label>
          <select className="form-select form-select-sm" value={month} onChange={e => setMonth(+e.target.value)}>
            {MONTHS_HE.slice(1).map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
        </Col>
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.YEAR')}</label>
          <select className="form-select form-select-sm" value={year} onChange={e => setYear(+e.target.value)}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Col>
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.GROUP_BY')}</label>
          <select className="form-select form-select-sm" value={groupBy} onChange={e => setGroupBy(e.target.value)}>
            <option value="employee">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.GROUP_EMPLOYEE')}</option>
            <option value="group">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.GROUP_CONCENTRATION')}</option>
            <option value="none">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.GROUP_NONE')}</option>
          </select>
        </Col>
        <Col xs={3}>
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.SEARCH')}</label>
          <InputGroup size="sm">
            <InputGroupText><i className="ri-search-line" /></InputGroupText>
            <Input placeholder={t('PROFITABILITY.MONTHLY_REPORT_PAGE.SEARCH_PLACEHOLDER')}
                   value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <Button size="sm" color="light" onClick={() => setSearch('')}>
                <i className="ri-close-line" />
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col xs="auto">
          <Button color="primary" size="sm" onClick={() => fetchReport(true)}>
            <i className="ri-refresh-line me-1" />{t('PROFITABILITY.COMMON.REFRESH_DATA')}
          </Button>
        </Col>
        <Col xs="auto">
          <Button color="success" size="sm" outline onClick={exportExcel} disabled={!list.length}>
            <i className="ri-file-excel-line me-1" />{t('PROFITABILITY.COMMON.EXPORT_EXCEL')}
          </Button>
        </Col>
        <Col>
          <div className="text-muted text-end small">
            {MONTHS_HE[month]} {year} — {filtered.length} / {list.length} {t('PROFITABILITY.COMMON.TERMINALS')}
          </div>
        </Col>
      </Row>

      {filtered.length === 0 ? (
        <NoRecords />
      ) : (
        <div className="table-responsive">
          <Table bordered hover size="sm" className="text-nowrap align-middle">
            <thead className="table-dark">
              <tr>
                {groupBy === 'none' && <Th field="employeeName">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.EMPLOYEE')}</Th>}
                <Th field="terminalNumber">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.TERMINAL')}</Th>
                <Th field="locationName">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.LOCATION')}</Th>
                <Th field="transactions" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.TRANSACTIONS')}</Th>
                <Th field="amountProcessed" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.AMOUNT_PROCESSED')}</Th>
                <Th field="revenue" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.REVENUE')}</Th>
                <Th field="locationCost" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.LOCATION_COST')}</Th>
                <Th field="casponetCost" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.CASPONET')}</Th>
                <Th field="costOfMoney" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.COST_OF_MONEY')}</Th>
                <Th field="commCost" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.COMM')}</Th>
                <Th field="totalCost" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.TOTAL_COST')}</Th>
                <Th field="profit" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.MACHINE_PROFIT')}</Th>
                <Th field="profitPct" className="text-end">{t('PROFITABILITY.MONTHLY_REPORT_PAGE.TABLE.PCT')}</Th>
              </tr>
            </thead>
            <tbody>
              {grouped
                ? Object.entries(grouped).map(([grpLabel, rows]) => (
                    <React.Fragment key={grpLabel}>
                      {renderGroupHeader(grpLabel, rows)}
                      {rows.map((r, i) => renderRow(r, `${grpLabel}-${i}`))}
                    </React.Fragment>
                  ))
                : sorted.map((r, i) => renderRow(r, i))}
            </tbody>
            <tfoot>
              <tr className="table-dark fw-bold">
                <td colSpan={groupBy === 'none' ? 3 : 2}>
                  {t('PROFITABILITY.MONTHLY_REPORT_PAGE.FOOTER.GRAND_TOTAL')}
                  {search && <Badge color="warning" className="ms-2">{filtered.length} {t('PROFITABILITY.MONTHLY_REPORT_PAGE.FOOTER.RESULTS')}</Badge>}
                </td>
                <td className="text-end">{totals.transactions?.toLocaleString()}</td>
                <td className="text-end">{fmt(totals.amountProcessed)}</td>
                <td className="text-end">{fmt(totals.revenue)}</td>
                <td className="text-end">{fmt(totals.locationCost)}</td>
                <td className="text-end">{fmt(totals.casponetCost)}</td>
                <td className="text-end">{fmt(totals.costOfMoney)}</td>
                <td className="text-end">{fmt(totals.commCost)}</td>
                <td className="text-end">{fmt(totals.totalCost)}</td>
                <td className={`text-end ${totals.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                  {fmt(totals.profit)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </Table>
        </div>
      )}
    </React.Fragment>
  );
};

export default MonthlyReport;
